import React, { useEffect, useRef, useState } from 'react';
import styles from './js-compiler.module.scss';
import { useSystemSounds } from '../../../../hooks/system-sounds/useSystemSounds';

const defaultCode = `// JavaScript Compiler
function greet(name){
  return "Hello " + name;
}

console.log(greet("Sunil"));
`;

const JSCompiler: React.FC = () => {
  const { play } = useSystemSounds();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [code, setCode] = useState(defaultCode);
  const [output, setOutput] = useState('');

  /* =========================
     Listen iframe messages
  ========================== */
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (!event.data) return;

      if (event.data.type === 'log') {
        setOutput((prev) => prev + event.data.message + '\n');
      }

      if (event.data.type === 'error') {
        setOutput((prev) => prev + '❌ ' + event.data.message + '\n');
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  /* =========================
     Compile + Run
  ========================== */
  const runCode = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    play('click');

    setOutput('');

    const html = `
      <!DOCTYPE html>
      <html>
      <body>
        <script>
          function send(type, message){
            parent.postMessage({type, message}, "*");
          }

          console.log = (...args)=>{
            send("log", args.join(" "));
          };

          const userCode = \`${code.replace(/`/g, '\\`')}\`;

          /* ===== COMPILATION STEP ===== */
          let compiled;

          try {
            compiled = new Function(userCode);
          } catch(e){
            send(
              "error",
              "Compilation Error: " + e.message
            );
          }

          /* ===== EXECUTION STEP ===== */
          if(compiled){
            try{
              compiled();
            }catch(err){
              send(
                "error",
                "Runtime Error: " + err.message
              );
            }
          }
        </script>
      </body>
      </html>
    `;

    iframe.srcdoc = html;
  };

  const clearCode = () => {
    play('click');
    setOutput('');
  };

  return (
    <div className={styles.compiler}>
      <div className={styles.toolbar}>
        <button onClick={runCode}>▶ Run</button>
        <button onClick={clearCode}>🧹 Clear</button>
      </div>

      <textarea
        className={styles.editor}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
        autoComplete="off"
      />

      <div className={styles.output}>
        <pre>{output}</pre>
      </div>

      <iframe
        ref={iframeRef}
        sandbox="allow-scripts"
        title="sandbox"
        className={styles.hiddenFrame}
      />
    </div>
  );
};

export default JSCompiler;
